import { ImageVariant } from '-/Custom/Util.types';
import { Image, useImage } from 'expo-image';
import { requestPermissionsAsync } from 'expo-media-library/legacy';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useRef } from 'react';
import { Pressable, View } from 'react-native';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';
import { useChatDetailContext } from '@/components/Chat/ChatDetailContext';
import Spinner from '@/components/Custom/Spinner';
import Icon from '@/components/Unistyles/Icon';
import { graphql, type ResultOf } from '@/gql/graphql';
import useChatActionItems from '@/hooks/useChatActionItems';
import useChatMediaAsset from '@/hooks/useChatMediaAsset';
import useGenerateImageUploadUrls from '@/hooks/useGenerateImageUploadUrls';
import useHaptics from '@/hooks/useHaptics';
import useUploadMessageImage from '@/hooks/useUploadMessageImage';
import useUserId from '@/hooks/useUserId';
import type { ChatMediaCacheAsset } from '@/stores/useChatMediaCacheStore';
import { setUploadMediaMeta, useChatStore } from '@/stores/useChatStore';
import { LONG_PRESS_DELAY } from '@/utils/constants';
import getCloudflareMediaUrl from '@/utils/getCloudflareMediaUrl';

const IMAGE_TRANSITION_DURATION = 500;
const IMAGE_RETRY_DELAY = 1000;

const ChatMediaUploadedItemFragment = graphql(/* GraphQL */ `
  fragment ChatMediaUploadedItem on message_media {
    id
    media_id
    created_by
  }
`);

const UnistylesImage = withUnistyles(Image);

export default function ChatMediaUploadedItem({
  id,
  mediaId,
  pendingAsset,
  variant,
  createdBy,
  isAlbum = false,
  albumWidth,
}: {
  id: ResultOf<typeof ChatMediaUploadedItemFragment>['id'];
  mediaId: ResultOf<typeof ChatMediaUploadedItemFragment>['media_id'];
  pendingAsset?: ChatMediaCacheAsset | null;
  variant: ImageVariant | number;
  createdBy: ResultOf<typeof ChatMediaUploadedItemFragment>['created_by'];
  isAlbum?: boolean;
  albumWidth?: number;
}) {
  const { push } = useRouter();
  const { chatId } = useLocalSearchParams();
  const userId = useUserId();
  const generateImageUploadUrls = useGenerateImageUploadUrls();
  const bottomSheetRef = useChatDetailContext((state) => state.bottomSheetRef);
  const inputRef = useChatDetailContext((state) => state.inputRef);
  const getActionItems = useChatActionItems();

  // TODO Fix this for device type
  const url = mediaId
    ? getCloudflareMediaUrl({
        id: mediaId,
        variant,
      })
    : null;

  const onPress = () => {
    const publicUrl = mediaId
      ? getCloudflareMediaUrl({
          id: mediaId,
          variant: ImageVariant.Hd,
        })
      : null;

    push({
      pathname: '/chat/[chatId]/media',
      params: { chatId, url: publicUrl },
    });
  };

  const uploadMessageImage = useUploadMessageImage();

  const mediaMeta = useChatStore((chatStoreState) =>
    chatStoreState.uploadMediaMeta.get(id),
  );

  const localAsset = useChatMediaAsset(id) || pendingAsset || null;
  const localPreviewUri = localAsset?.path;
  const shouldShowUploadingOverlay = !url && mediaMeta?.isUploading;

  const shouldShowErrorOverlay =
    !url && !mediaMeta?.isUploading && createdBy === userId && !!localAsset;

  const onPressError = async () => {
    if (process.env.EXPO_OS !== 'web') {
      await requestPermissionsAsync();
    }

    if (localAsset) {
      setUploadMediaMeta(id, { isUploading: true });

      const { data: uploadUrls } = await generateImageUploadUrls(1);
      const uploadUrl = uploadUrls?.generateImageUploadUrls?.[0]?.uploadURL;

      if (uploadUrl) {
        uploadMessageImage(id, `/message/${chatId}/${id}`, uploadUrl);
      }
    }
  };

  const imageSource = url || localPreviewUri || '';
  const retriedRemoteUrlRef = useRef<string | null>(null);
  const image = useImage(imageSource, {
    onError: (_error, retry) => {
      if (url && retriedRemoteUrlRef.current !== url) {
        retriedRemoteUrlRef.current = url;
        setTimeout(retry, IMAGE_RETRY_DELAY);
      }
    },
  });

  const onLongPress = useHaptics(() => {
    if (url) {
      inputRef?.current?.blur();
      bottomSheetRef?.current?.present(
        getActionItems(
          id,
          getCloudflareMediaUrl({
            id: mediaId,
            variant: ImageVariant.Public,
          }),
          createdBy,
          true,
        ),
      );
    }
  });

  return (
    <Pressable
      onPress={onPress}
      onLongPress={onLongPress}
      style={({ pressed }) => style.container(pressed, isAlbum, albumWidth)}
      delayLongPress={LONG_PRESS_DELAY}
    >
      <UnistylesImage
        transition={IMAGE_TRANSITION_DURATION}
        contentFit="cover"
        source={image}
        style={style.image}
      />
      {shouldShowErrorOverlay ? (
        <Pressable onPress={onPressError} style={style.overlay}>
          <Icon size="4xl" variant="white" name="refresh-outline" />
        </Pressable>
      ) : shouldShowUploadingOverlay ? (
        <View style={style.overlay}>
          <Spinner size="lg" />
        </View>
      ) : null}
    </Pressable>
  );
}

const style = StyleSheet.create((theme) => ({
  container: (pressed: boolean, isAlbum: boolean, albumWidth?: number) => {
    const resolvedAlbumWidth = albumWidth ?? 100;

    return {
      backgroundColor: 'transparent',
      opacity: pressed ? 0.7 : 1,
      borderRadius: isAlbum ? 0 : theme.borderRadius.xl,
      overflow: isAlbum ? 'visible' : 'hidden',
      height: isAlbum ? 100 : 240,
      width: isAlbum ? resolvedAlbumWidth : '80%',
      maxWidth: isAlbum ? resolvedAlbumWidth : 300,
    };
  },
  overlay: {
    height: '100%',
    width: '100%',
    position: 'absolute',
    opacity: 0.6,
    backgroundColor: theme.colors.black,
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: {
    flex: 1,
    width: '100%',
  },
}));
